import type { EvaluationDetail, VerdictRow } from './api'
import { subjectText } from './limits'

/**
 * What a verdict came to, in words — TECHNICAL_DESIGN.md §6.4.
 *
 * Three surfaces say it: a run's verdicts in the application, the Excel verdict
 * pane, and the exported file. The first two share this module, which carries no
 * React, so the pane stays small. The file is written by `verdictreport.py`, and
 * `__tests__/verdict_words.json` holds the Python and these functions to the same
 * sentences.
 */

/** `braeton.single_corporate_issuer` as the workbook row it came from. */
export function rowName(ruleKey: string): string {
  const row = ruleKey.includes('.') ? ruleKey.slice(ruleKey.indexOf('.') + 1) : ruleKey
  const words = row.replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/** The day holdings were as at, as the file stated it — not moved into the reader's
 *  time zone, where a date at midnight UTC becomes the day before. */
export function asAt(instant: string): string {
  return new Date(instant).toISOString().slice(0, 10)
}

export function amount(value: number | string | null | undefined, unit: string | null): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  switch (unit) {
    case 'percent':
      return `${value.toFixed(2)}%`
    case 'years':
      return `${value.toFixed(2)} years`
    case 'days':
      return `${Math.round(value)} days`
    default:
      return value.toLocaleString(undefined, { maximumFractionDigits: 2 })
  }
}

/** The room left, or how far past the limit — in points, notches or the measure's unit. */
export function room(v: Pick<VerdictRow, 'headroom' | 'unit'>): string {
  if (v.headroom === null || v.headroom === undefined) return ''
  const size = Math.abs(v.headroom)
  const said =
    v.unit === 'percent'
      ? `${size.toFixed(2)} pts`
      : v.unit === 'notch'
        ? `${Number(size.toFixed(2))} notch${size === 1 ? '' : 'es'}`
        : amount(size, v.unit)
  return v.headroom < 0 ? `over by ${said}` : `${said} to spare`
}

/** Which holdings a verdict judged. */
export function covers(v: Pick<VerdictRow, 'template' | 'subject'>): string {
  if (v.template === 'rating_floor') {
    return v.subject?.benchmark === 'outside' ? 'Every holding outside the benchmark' : 'Every holding'
  }
  if (v.template === 'average_rating_floor') return 'The fund, on average'
  const said = subjectText(v.subject)
  return said.charAt(0).toUpperCase() + said.slice(1)
}

/** What a missing input is, in the words of the screen that supplies it. */
function needed(input: string): string {
  if (input === 'in_benchmark') return 'a column saying which holdings are in the benchmark, mapped when the snapshot is loaded'
  if (input === 'benchmark') return 'the benchmark’s figures for the snapshot’s day, such as its duration or its weight in each country'
  const match = /^(class|rating|analytic):(\S+)(?: on (.+))?$/.exec(input)
  if (!match) return input.replace(/_/g, ' ')
  const [, kind, name, where] = match
  const called = kind === 'rating' ? 'the agencies’ ratings' : name!.replace(/_/g, ' ')
  if (where) return `${called}, blank on ${where}`
  if (kind === 'rating') return 'the agencies’ rating columns mapped when the snapshot is loaded'
  return `a column mapped to ${called} when the snapshot is loaded`
}

/** Why a verdict is what it is: within the tolerance, outside the limit, or what would decide it. */
export function why(
  v: Pick<VerdictRow, 'state' | 'reason' | 'missing_inputs' | 'unit'>,
  run: Pick<EvaluationDetail, 'warn_tolerance' | 'notch_tolerance'>,
): string {
  if (v.state === 'not_evaluable') {
    const reason = v.reason ? v.reason.charAt(0).toUpperCase() + v.reason.slice(1) + '.' : ''
    const needs = [...new Set(v.missing_inputs.map(needed))]
    return [reason, needs.length ? `Needs ${needs.join('; ')}.` : ''].filter(Boolean).join(' ')
  }
  const rating = v.unit === 'notch'
  // A floor read through a sell-by period says which holding fell, when, and until when.
  const said = v.reason ? v.reason.charAt(0).toUpperCase() + v.reason.slice(1) + '.' : ''
  if (v.state === 'warn') {
    if (said) return said
    return rating
      ? `Within ${run.notch_tolerance} notch${run.notch_tolerance === 1 ? '' : 'es'} of the floor.`
      : `Within ${Math.round(run.warn_tolerance * 100)}% of the limit.`
  }
  if (v.state === 'breach') return said || (rating ? 'Below the floor.' : 'Outside the limit.')
  return ''
}
